<template>
    <b-modal
        id="assessment-join-modal"
        size="lg"
        title="평가 참여자 등록"
        body-class="bg-white"
        centered
        :no-close-on-backdrop="isLoading"
        :no-close-on-esc="isLoading"
        @hidden="onHidden"
    >
        <template #default>
            <p class="mb-3">
                <template v-if="targetUsers.length === 1">
                    <strong>{{ targetUsers[0].user_name }}</strong>님을 평가에 참여시킬 수 있습니다.
                </template>
                <template v-else>
                    <strong>{{ targetUsers.length }}명</strong>의 사용자를 평가에 참여시킬 수 있습니다.
                </template>
            </p>
            <b-table
                ref="projectTable"
                :items="projects"
                :fields="tableFields"
                selectable
                select-mode="multi"
                class="mb-0 custom-table"
                sticky-header="400px"
                show-empty
                empty-text="참여 중인 평가가 없습니다."
                @row-selected="onRowSelected"
            >
                <!-- 체크박스 클릭은 b-table 이 행 클릭으로 보지 않으므로 직접 선택을 바꾼다 -->
                <template #cell(selected)="{ rowSelected, selectRow, unselectRow }">
                    <b-form-checkbox :checked="rowSelected" @change="checked => checked ? selectRow() : unselectRow()" />
                </template>
                <template #cell(status_code)="data">
                    <b-badge :variant="getProgressColor(data.item)">
                        {{ progressLabel(data.item) }}
                    </b-badge>
                </template>
            </b-table>
        </template>

        <template #modal-footer>
            <b-button variant="outline-secondary" @click="$bvModal.hide('assessment-join-modal')">
                건너뛰기
            </b-button>
            <b-button
                variant="primary"
                :disabled="selectedProjects.length === 0 || isLoading"
                @click="joinAssessments"
            >
                <b-spinner v-if="isLoading" small class="mr-1" />
                참여 등록
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import assessmentCommonMixin from '@shared/mixin/assessment.common.mixin';
import { getMemberErrorText, PROGRESS_STATUS_LABELS } from '@shared/components/admin/pages/Management/Company/data';

export default {
    name: 'AssessmentJoinModal',
    mixins: [assessmentCommonMixin],
    props: {
        companyId: {
            type: Number,
            required: true,
        },
        projects: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            targetUsers: [],
            selectedProjects: [],
            isLoading: false,
        };
    },
    computed: {
        tableFields() {
            return [
                {
                    key: 'selected',
                    label: '',
                    thStyle: { width: '40px' },
                    thClass: ['text-center'],
                    tdClass: ['text-center'],
                },
                {
                    key: 'project_code',
                    label: '프로젝트',
                },
                {
                    key: 'period',
                    label: '참여기간',
                    thStyle: { width: '100px' },
                    thClass: ['text-center'],
                    tdClass: ['text-center'],
                },
                {
                    key: 'industry_name',
                    label: '평가산업',
                    thStyle: { width: '120px' },
                    thClass: ['text-center'],
                    tdClass: ['text-center'],
                },
                {
                    key: 'status_code',
                    label: '상태',
                    thStyle: { width: '100px' },
                    thClass: ['text-center'],
                    tdClass: ['text-center'],
                },
            ];
        },
    },
    methods: {
        progressLabel(item) {
            const status = this.getProgressStatus(item);
            return PROGRESS_STATUS_LABELS[status] || status;
        },
        show(users) {
            if (!this.projects.length) {
                return;
            }
            this.targetUsers = users;
            this.selectedProjects = [];
            if (this.$refs.projectTable) {
                this.$refs.projectTable.clearSelected();
            }
            this.$bvModal.show('assessment-join-modal');
        },
        onRowSelected(rows) {
            this.selectedProjects = rows;
        },
        onHidden() {
            this.targetUsers = [];
            this.selectedProjects = [];
            this.isLoading = false;
        },
        async joinAssessments() {
            this.isLoading = true;
            try {
                const promises = [];
                // promises 와 같은 순서의 (사용자, 프로젝트) — 실패 건을 이름으로 알린다
                const labels = [];
                this.selectedProjects.forEach((project) => {
                    this.targetUsers.forEach((user) => {
                        labels.push(`${user.user_name} - ${project.project_code}`);
                        promises.push(
                            this.$axios.post('/admin/assessment/member/join', {
                                campaign_id: project.campaign_id,
                                company_id: this.companyId,
                                user_id: user.user_id,
                            })
                        );
                    });
                });
                const results = await Promise.allSettled(promises);
                const failures = results.reduce((acc, r, idx) => {
                    if (r.status === 'rejected') acc.push(`${labels[idx]}: ${getMemberErrorText(r.reason, '등록 실패')}`);
                    return acc;
                }, []);
                const succeededCount = results.length - failures.length;

                if (failures.length === 0) {
                    this.$EventBus.$emit('toast-success', '평가 참여 등록', '평가 참여 등록이 완료되었습니다.');
                    this.$bvModal.hide('assessment-join-modal');
                    this.$emit('completed');
                } else {
                    const message = succeededCount > 0
                        ? `일부 등록이 실패했습니다. (성공 ${succeededCount}건, 실패 ${failures.length}건) 다시 시도해도 성공한 항목은 중복 적용되지 않습니다. ${failures.join(' / ')}`
                        : `평가 참여 등록이 실패했습니다. ${failures.join(' / ')}`;
                    this.$EventBus.$emit(succeededCount > 0 ? 'toast-warning' : 'toast-failure', '평가 참여 등록', message);
                }
            } catch (e) {
                console.error(e);
                this.$EventBus.$emit('toast-failure', '평가 참여 등록', '평가 참여 등록 중 오류가 발생했습니다.');
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>
